import React, { useEffect, useRef } from 'react';

export default function EnrichmentWarningPopup({ country, enrichmentLevel, onClose }) {
  const alarmRef = useRef(null);
  
  // Determine warning level
  const isUrgent = enrichmentLevel >= 90;
  const warningColor = isUrgent ? '#ef4444' : '#f59e0b';
  const warningText = isUrgent ? 'CRITICAL ALERT' : 'WARNING';
  
  // Handle close with logging
  const handleClose = () => {
    console.log('🔘 ACKNOWLEDGE button clicked - closing enrichment warning');
    if (alarmRef.current) {
      alarmRef.current.pause();
      alarmRef.current.currentTime = 0;
    }
    onClose();
  };
  
  useEffect(() => {
    // ESC key handler
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    
    // Play alarm sound
    const alarmSound = isUrgent 
      ? '/assets/audio/nuke-silo-alert.mp3' 
      : '/assets/audio/nuke-launch-alarm.mp3';
    
    const alarm = new Audio(alarmSound);
    alarm.volume = 0.8;
    alarm.loop = true; // Loop the alarm
    alarmRef.current = alarm;
    
    alarm.play().catch(e => console.log('Alarm sound failed:', e));
    console.log(`🚨 Playing ${isUrgent ? 'CRITICAL' : 'WARNING'} alarm for ${country.name} at ${enrichmentLevel}%`);
    
    // Auto-close after 8 seconds (give user time to read)
    const timer = setTimeout(() => {
      console.log('⏰ Auto-closing enrichment warning after 8 seconds');
      handleClose();
    }, 8000);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleEsc);
      if (alarmRef.current) {
        alarmRef.current.pause();
        alarmRef.current.currentTime = 0;
      }
    };
  }, [country.name, enrichmentLevel, isUrgent]); // Remove onClose from dependencies
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 animate-pulse pointer-events-auto"
      onClick={handleClose}
      style={{ animationDuration: isUrgent ? '0.5s' : '1s' }}
    >
      <div 
        className="relative p-12 rounded-2xl border-4 max-w-2xl w-full mx-4 animate-shake pointer-events-auto"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          borderColor: warningColor,
          boxShadow: `0 0 60px ${warningColor}, inset 0 0 30px ${warningColor}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Warning Symbol */}
        <div className="text-center mb-6">
          <div 
            className="text-9xl mb-4 animate-bounce"
            style={{ 
              color: warningColor,
              textShadow: `0 0 30px ${warningColor}`,
              animationDuration: isUrgent ? '0.5s' : '1s'
            }}
          >
            ⚠️
          </div>
          
          {/* Warning Level */}
          <div 
            className="text-5xl font-black uppercase tracking-wider mb-2"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              color: warningColor,
              textShadow: `0 0 20px ${warningColor}`,
            }}
          >
            {warningText}
          </div>
          
          {/* Enrichment Bar */}
          <div className="text-sm text-gray-400 mb-4 uppercase tracking-wide">
            Nuclear Enrichment Alert
          </div>
        </div>
        
        {/* Country Info */}
        <div className="text-center mb-6">
          <div className="text-3xl font-bold text-white mb-2">
            {country.name}
          </div>
          <div className="text-xl text-gray-300 mb-4">
            has reached
          </div>
          <div 
            className="text-7xl font-black"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              color: warningColor,
              textShadow: `0 0 30px ${warningColor}`,
            }}
          >
            {enrichmentLevel.toFixed(0)}%
          </div>
          <div className="text-xl text-gray-300 mt-4">
            URANIUM ENRICHMENT
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-800 rounded-full h-8 overflow-hidden border-2 border-gray-600">
            <div 
              className="h-full transition-all duration-1000 flex items-center justify-end pr-4"
              style={{
                width: `${enrichmentLevel}%`,
                background: isUrgent 
                  ? 'linear-gradient(to right, #dc2626, #ef4444, #fca5a5)'
                  : 'linear-gradient(to right, #f59e0b, #fbbf24, #fde047)',
                boxShadow: `0 0 20px ${warningColor}`
              }}
            >
              <span className="text-white font-bold text-sm">
                {enrichmentLevel.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
        
        {/* Threat Assessment */}
        <div 
          className="text-center p-4 rounded-lg mb-6"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
        >
          <div className="text-xl font-bold mb-2" style={{ color: warningColor }}>
            {isUrgent ? '☢️ NUCLEAR CAPABILITY IMMINENT ☢️' : '⚠️ APPROACHING NUCLEAR CAPABILITY ⚠️'}
          </div>
          <div className="text-sm text-gray-300">
            {isUrgent 
              ? `${country.name} is on the verge of producing nuclear weapons!`
              : `${country.name} is advancing toward nuclear weapons capability!`
            }
          </div>
        </div>
        
        {/* Close Button */}
        <div className="text-center pointer-events-auto">
          <button
            onClick={handleClose}
            className="px-8 py-3 text-lg font-bold uppercase rounded-lg transition-all hover:scale-105 cursor-pointer pointer-events-auto"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              backgroundColor: warningColor,
              color: '#ffffff',
              boxShadow: `0 4px 12px ${warningColor}`,
            }}
          >
            ACKNOWLEDGE
          </button>
          <div className="text-xs text-gray-400 mt-2">
            Click button, press ESC, or click outside to close
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        .animate-shake {
          animation: shake 0.5s infinite;
        }
      `}</style>
    </div>
  );
}
